/**
 * The research record store as its routes, tools and views exchange it (ADR-0085): one revision store
 * for a project's research and for each person's unassigned inbox, with the evidence domain, the
 * report verification domain (ADR-0086), the project's research map (ADR-0089), the analysis domain
 * (ADR-0088) and the feedback domain (ADR-0089) on it. See CONTEXT.md "Research record" and "Research map".
 */

export const RESEARCH_SCHEMA_VERSION = 1;

/** The research tool group's tools, as the runtime registers them and the chat draws them. */
export const RESEARCH_READ = "research_read";
export const RESEARCH_PROPOSE = "research_propose";
export const RESEARCH_FINALIZE_REPORT = "research_finalize_report";

/** Where a record belongs. The owner is derived from the signed-in person or the session, never sent. */
export type ResearchScopeRef = { kind: "project"; projectId: string } | { kind: "unassigned"; collectionId: "inbox" };

export type RecordStatus = "proposed" | "confirmed" | "rejected" | "superseded";
export type RecordAuthority = "agent_suggested" | "user_confirmed" | "unknown";

/** Who wrote a revision and through what; everything but `kind` and `reason` is filled in by the service. */
export interface RecordOrigin {
	kind: "agent" | "user" | "import";
	reason?: string;
	actor: string;
	channel: "http" | "tool";
	sessionId?: string;
	toolCallId?: string;
	at: number;
}

/** What an owner decision was made through. */
export type DecisionSource = { kind: "ui_action"; action: "confirm" | "reject" | "acknowledge"; path: string };

/** A reference a record carries: another record, a file, a URL, a citekey or a tool handle. */
export type ResearchRef = { kind: string; id?: string; revision?: number } & Record<string, unknown>;

export interface ResearchRecord<B = Record<string, unknown>> {
	schemaVersion: number;
	id: string;
	kind: string;
	scopeRef: ResearchScopeRef;
	projectId: string | null;
	owner: string;
	revision: number;
	status: RecordStatus;
	authority: RecordAuthority;
	origin: RecordOrigin;
	refs: ResearchRef[];
	body: B;
	createdAt: number;
	contentHash: string;
	confirmationActor?: string;
	confirmationSource?: DecisionSource;
	confirmedRevision?: number;
	confirmedAt?: number;
	rejectionReason?: string;
	rejectedBy?: string;
	rejectedAt?: number;
	unsupported?: undefined;
}

/** A revision written by a schema this server does not know: shown, never rewritten. */
export interface UnsupportedRecord {
	unsupported: true;
	schemaVersion: number;
	id: string;
	kind: string;
	scopeRef: ResearchScopeRef;
	revision: number;
	status: string;
	createdAt: number;
	reason: string;
}

export type StoredRecord = ResearchRecord | UnsupportedRecord;

/** A record as it reads: its effective revision, and the newest one when that is a different, unconfirmed one. */
export interface RecordRead {
	record: StoredRecord;
	latest?: StoredRecord;
}

export const RESEARCH_DOMAINS = ["decisions", "evidence", "analysis", "feedback", "verification", "map"] as const;
export type ResearchDomain = (typeof RESEARCH_DOMAINS)[number];

export interface DomainCapability {
	available: boolean;
	reason?: string;
	operations?: string[];
	/** Analysis only: whether managed execution would run here now, under the server's isolation control. */
	execution?: { available: boolean; isolation: "auto" | "unavailable"; backend?: string; reason?: string };
}

export interface ResearchCapabilities {
	schemaVersion: number;
	revision: string;
	scopeRef: ResearchScopeRef;
	domains: Record<ResearchDomain, DomainCapability>;
}

/** Something a view must show rather than leave out: an unavailable domain, an unsupported record, a limit. */
export interface ResearchNotice {
	kind: "domain_unavailable" | "unsupported_schema" | "extraction_limit" | "source_problem" | "not_associated" | "scope_required" | "execution_blocked" | "method_not_assessed" | "direction_version_mismatch" | "truncated" | "clipped" | "error";
	message: string;
	domain?: ResearchDomain;
	recordId?: string;
	/** source_problem: the source's path and what it looks like now. */
	path?: string;
	sourceStatus?: SourceStatus;
	revision?: number;
}

// The evidence domain: original passage -> claim -> manuscript anchor.

/** What a relative path is relative to. */
export type WorkspaceBase = { kind: "profile"; profile: string } | { kind: "project"; projectId: string } | { kind: "session"; sessionId: string };

export type SourceStatus = "current" | "stale" | "missing" | "moved" | "ambiguous" | "unlocated" | "unretrieved";

/** A file as a record keeps it: the authorized original path and the snapshot retained of it. */
export interface FileRef {
	path: string;
	base: WorkspaceBase;
	/** The snapshot's SHA-256; absent when nothing could be read. */
	sha256?: string;
	/** The digest the writer stated, kept when it differs from what was read. */
	declaredSha256?: string;
	artifactId?: string;
	size?: number;
	capturedAt?: number;
	lastCheckedAt: number;
	sourceStatus: SourceStatus;
	/** Where in the file, as the writer named it: a page, section or line range. */
	locator?: string;
	/** Where a file found under another path with the snapshot's content is now. */
	movedTo?: string;
}

export interface SourceBody {
	title: string;
	/** As the writer reported it; the platform's own observation is `sourceStatus`. */
	existence: "retrieved" | "unavailable" | "unknown";
	fileRef?: FileRef;
	url?: string;
	citekey?: string;
	handle?: string;
	sourceStatus: SourceStatus;
	limitations: string[];
	[extra: string]: unknown;
}

export interface ClaimBody {
	text: string;
	[extra: string]: unknown;
}

export const DRAFT_FORMATS = ["markdown", "text", "latex", "pdf", "docx", "docs"] as const;
export type DraftFormat = (typeof DRAFT_FORMATS)[number];

/** Registration states of an anchor, then what a later check finds. */
export type AnchorStatus = "located" | "unlocated" | "unsupported" | "ambiguous" | "unretrieved" | "current" | "stale" | "missing" | "moved";

export type AnchorLocator = { type: "lines"; start: number; end: number; paragraph: number } | { type: "extracted"; paragraph: number; page?: number; extractionSha256: string } | { type: "none" };

export interface DraftAnchor {
	id: string;
	quote: string;
	locator: AnchorLocator;
	/** SHA-256 of the anchored text. */
	hash: string;
	claimIds: string[];
	anchorStatus: AnchorStatus;
}

export interface DraftBody {
	title?: string;
	format: DraftFormat | "unknown";
	declaredFormat: string;
	originalRef: (FileRef & { sourceId?: string }) | { url: string; sourceStatus: SourceStatus; lastCheckedAt: number };
	extractedTextRef?: FileRef;
	anchorStatus: AnchorStatus;
	anchors: DraftAnchor[];
	limitations: string[];
	/** What registration found wrong with the input: an invalid format, an anchor it could not place. */
	findings: string[];
	[extra: string]: unknown;
}

export type EvidenceReading = "quote" | "paraphrase" | "inference";
export type EvidenceRelation = "supports" | "contradicts" | "qualifies" | "context";
export type PassageLocator = { type: "lines"; start: number; end: number };

export interface EvidenceBody {
	sourceId: string;
	sourceRevision: number;
	claimId: string;
	claimRevision: number;
	passage: { text: string; locator: PassageLocator; hash: string; path: string; snapshotSha256: string; artifactId?: string };
	reading: EvidenceReading;
	relation: EvidenceRelation;
	conditions: string;
	limitations: string;
	/** A quote is checked against the snapshot's bytes; a paraphrase or an inference is not a quotation. */
	quoteCheck: { status: "matched" | "not_applicable"; checkedAt: number; snapshotSha256: string };
	/** Linking proves the passage was retrieved, never that it entails the claim. */
	receipt: { semanticSupportEstablished: false };
}

export type ExistenceJudgement = "confirmed" | "not_found" | "unknown";
export type SupportJudgement = "supported" | "partially_supported" | "not_supported" | "contradicted" | "unknown";

export interface AssessmentBody {
	claimId: string;
	claimRevision: number;
	evidence: Array<{ id: string; revision: number; sourceId: string; sourceRevision: number; snapshotSha256: string }>;
	existence: ExistenceJudgement;
	support: SupportJudgement;
	rationale: string;
	checks: string[];
	judgement: "agent_assessed";
	assessor: string;
}

/** The revisions and digests an anchor's review covered; an acknowledgment binds exactly these. */
export interface ReviewedRevisions {
	draft: { id: string; revision: number; anchorHash: string | null };
	sources: Array<{ id: string; revision: number; sha256: string | null }>;
	claims: Array<{ id: string; revision: number }>;
	evidence: Array<{ id: string; revision: number }>;
	assessments: Array<{ id: string; revision: number }>;
}

export interface AcknowledgmentBody {
	draftId: string;
	anchorId: string;
	reviewed: ReviewedRevisions;
	note?: string;
}

export type ClaimSupport = "unverified" | "supported" | "partially_supported" | "not_supported" | "contradicted";

export interface EvidenceView {
	id: string;
	revision: number;
	status: RecordStatus;
	authority: RecordAuthority;
	sourceId: string;
	sourceTitle: string;
	passage: EvidenceBody["passage"];
	reading: EvidenceReading;
	relation: EvidenceRelation;
	conditions: string;
	limitations: string;
	quoteCheck: EvidenceBody["quoteCheck"];
	/** False once its source, claim or snapshot moved on from what was linked. */
	current: boolean;
	sourceStatus: SourceStatus;
}

export interface ClaimSummary {
	id: string;
	revision: number;
	status: RecordStatus;
	authority: RecordAuthority;
	text: string;
	support: ClaimSupport;
	/** Whose judgement a support other than unverified rests on. */
	supportBasis?: "agent_assessed" | "user_confirmed";
	supportReason: string;
	existence: ExistenceJudgement;
	evidence: EvidenceView[];
	assessment?: { id: string; revision: number; existence: ExistenceJudgement; support: SupportJudgement; rationale: string; judgement: "agent_assessed"; authority: RecordAuthority; current: boolean };
	anchors: Array<{ draftId: string; anchorId: string; anchorStatus: AnchorStatus }>;
}

export interface SourceSummary {
	id: string;
	revision: number;
	title: string;
	declaredExistence: SourceBody["existence"];
	/** The assessed existence; unknown until an assessment judged it. */
	existence: ExistenceJudgement;
	sourceStatus: SourceStatus;
	path?: string;
	url?: string;
	artifactId?: string;
	snapshotSha256?: string;
	capturedAt?: number;
	lastCheckedAt: number;
	limitations: string[];
}

export interface DraftSummary {
	id: string;
	revision: number;
	title?: string;
	format: DraftBody["format"];
	path?: string;
	artifactId?: string;
	anchorStatus: AnchorStatus;
	anchors: Array<{ id: string; quote: string; locator: AnchorLocator; anchorStatus: AnchorStatus; claimIds: string[] }>;
	limitations: string[];
	findings: string[];
}

export type ImpactStatus = "review_needed" | "stale" | "missing" | "moved";

export interface ImpactItem {
	draftId: string;
	draftRevision: number;
	anchorId: string;
	/** The anchor text as registered, from the retained snapshot. */
	quote: string;
	anchorStatus: AnchorStatus;
	status: ImpactStatus;
	sourceId?: string;
	claimId?: string;
	evidenceId?: string;
	reason: string;
	reasons: string[];
	/** Where the anchored text is now, when it moved. */
	movedTo?: AnchorLocator;
	draftArtifactId?: string;
	sourceArtifactId?: string;
	acknowledged: boolean;
	/** What an acknowledgment of this item binds. */
	current: ReviewedRevisions;
}

/** A paragraph that needs review, as the evidence summary lists it. */
export interface NeedsReviewItem {
	draftId: string;
	anchorId: string;
	sourceId?: string;
	claimId?: string;
	reason: string;
	status: ImpactStatus;
}

export interface EvidenceSummary {
	schemaVersion: number;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	claims: ClaimSummary[];
	sources: SourceSummary[];
	drafts: DraftSummary[];
	needsReview: NeedsReviewItem[];
	notices: ResearchNotice[];
}

export interface EvidenceImpacts {
	schemaVersion: number;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	paragraphs: ImpactItem[];
	notices: ResearchNotice[];
}

// The verification domain: a saved report finalized with its execution and check disclosure.

export const CHECK_TYPES = ["source_identity", "source_quote", "claim_support", "data_recompute", "human_judgment", "role_consensus"] as const;
export type CheckType = (typeof CHECK_TYPES)[number];

export const CHECK_STATUSES = ["executed", "not_run", "inconclusive", "failed"] as const;
export type CheckStatus = (typeof CHECK_STATUSES)[number];

/** The question types the selection protocol knows, each with the check it calls for. */
export const QUESTION_TYPE_CHECKS = {
	source_identity: "source_identity",
	quotation_matching: "source_quote",
	research_claim: "claim_support",
	numerical_reproducibility: "data_recompute",
	value_judgment: "human_judgment",
	role_consensus: "role_consensus",
} as const satisfies Record<string, CheckType>;
export type QuestionType = keyof typeof QUESTION_TYPE_CHECKS;

/** A file the report or a check names, with the snapshot the finalizer retained of it. */
export interface VerificationFileRef {
	path: string;
	sha256?: string;
	declaredSha256?: string;
	artifactId?: string;
	size?: number;
	capturedAt?: number;
	sourceStatus: "current" | "stale" | "missing" | "unretrieved";
	reason?: string;
}

/** What the platform itself observed of a session or job the report names: dispatch, nothing more. */
export interface ExecutionReceipt {
	kind: "session" | "job";
	id: string;
	relation: "this_session" | "delegate_child" | "delegate_job";
	profile?: string;
	model?: string;
	status?: string;
	/** A job's session, when it has one. */
	sessionId?: string;
	proves: "dispatch";
}

export interface ExecutionDisclosure {
	/** `unknown` unless the platform verified receipts; never the agent's claim. */
	mode: "unknown" | "single_session" | "dispatched_contexts";
	/** What the report's writer said about how it ran, kept apart and unverified. */
	claimedMode: string | null;
	claimed: Record<string, unknown> | null;
	receipts: ExecutionReceipt[];
	/** References that could not be verified, with why. */
	unverifiedRefs: Array<{ ref: unknown; reason: string }>;
	contextDispatch: "unknown" | "single_context" | "separate_contexts";
	siblingReadConstraint: "prompt_only" | "observed_violation" | "unknown";
	errorIndependence: "not_established";
	overlap: {
		models: string[];
		modelOverlap: "same_model" | "different_models" | "unknown";
		sources: string[];
		sourceOverlap: "shared_sources" | "distinct_sources" | "unknown";
	};
}

export interface VerificationCheck {
	id: string;
	type: CheckType;
	required: boolean;
	status: CheckStatus;
	reason?: string;
	method?: string;
	actor?: string;
	evidenceRefs: Array<VerificationFileRef | ResearchRef>;
	limitations?: string;
	verdict?: string;
	/** source_quote: the quotation as given, compared byte for byte against the source snapshot. */
	quote?: string;
	quoteMatch?: { matched: boolean; method: "exact_substring"; sourcePath?: string; sourceSha256?: string };
	/** source_quote and source_identity: matching or existence says nothing of support. */
	semanticSupport?: "not_assessed";
	/** claim_support: an agent's assessment stays attributed. */
	attribution?: "agent_assessment" | "person";
	recompute?: { inputs: unknown[]; outputs: unknown[]; comparison?: unknown };
	/** True when the finalizer added the check because a required one was missing. */
	added?: boolean;
}

export interface VerificationSelection {
	questionTypes: string[];
	requiredChecks: CheckType[];
	exclusions: Array<{ check: string; rationale: string }>;
	/** What the selection could not decide, such as an unknown question type. */
	gaps: string[];
}

export interface VerificationBoundary {
	name: string;
	level: "prompt_only" | "enforced" | "unknown";
	detail: string;
}

export interface VerificationReport {
	/** The persisted verification_report record. */
	id: string;
	revision: number;
	artifactId: string;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	input: VerificationFileRef & { sha256: string; snapshotArtifactId: string };
	questionType: string;
	questionTypes: string[];
	requiredChecks: CheckType[];
	selection: VerificationSelection;
	execution: ExecutionDisclosure;
	checks: VerificationCheck[];
	/** Executed checks other than role consensus. */
	externalValidation: Array<{ id: string; type: CheckType; verdict?: string }>;
	roleConsensus: Array<{ id: string; status: CheckStatus; verdict?: string }>;
	notRun: Array<{ id: string; type: CheckType; required: boolean; status: CheckStatus; reason: string }>;
	uncertainties: string[];
	boundaries: VerificationBoundary[];
	/** The Chinese disclosure section the artifact carries after the original bytes. */
	disclosure: string;
}

export const VERIFICATION_REPORT_KIND = "verification_report";

/** A verification report as its record keeps it. */
export function reportOf(record: ResearchRecord): VerificationReport {
	return { ...(record.body as unknown as Omit<VerificationReport, "id" | "revision" | "scopeRef">), id: record.id, revision: record.revision, scopeRef: record.scopeRef };
}

export interface VerificationSummary {
	schemaVersion: number;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	reports: Array<{
		id: string;
		revision: number;
		status: RecordStatus;
		artifactId: string;
		input: { path: string; sha256: string };
		snapshotAt: number;
		executionMode: ExecutionDisclosure["mode"];
		questionTypes: string[];
		counts: Record<CheckStatus, number>;
		externalValidation: number;
		requiredNotRun: number;
	}>;
	unsupported: number;
}

/**
 * Whether the bytes now at a saved report's path were finalized in the viewer's scope: what the report's document
 * view shows beside it. Only a finalization of exactly these bytes, with its artifact present, reads `finalized`.
 */
export type ReportFinalization =
	| {
			status: "finalized";
			path: string;
			sha256: string;
			scopeRef: ResearchScopeRef;
			report: { id: string; revision: number; artifactId: string; snapshotAt: number; executionMode: ExecutionDisclosure["mode"]; recordStatus: RecordStatus };
			artifactHref: string;
			pageHref: string;
	  }
	/** No finalization of these bytes: a legacy report, its verification unknown. */
	| { status: "legacy"; path: string; sha256: string; scopeRef: ResearchScopeRef }
	| { status: "missing"; path: string; scopeRef: ResearchScopeRef }
	/** The session's profile does not finalize reports. */
	| { status: "not_applicable" };

// The analysis domain: registered raw inputs, reported and observed environments, frozen plans and runs.

/** The managed snapshot against the digest it was registered with. */
export type InputIntegrity = "intact" | "corrupted" | "missing";

/** A registered raw input: an immutable managed snapshot, and apart what its original is now. */
export interface AnalysisInput {
	id: string;
	revision: number;
	path: string;
	base: WorkspaceBase;
	sha256: string;
	artifactId: string;
	size: number;
	capturedAt: number;
	integrity: InputIntegrity;
	/** The original against the snapshot, checked at read; a changed or removed original never changes the snapshot. */
	originalSourceStatus: SourceStatus;
	/** The original's digest now, when it differs from the snapshot's. */
	originalSha256?: string;
	lastCheckedAt: number;
	reason?: string;
}

/** An environment as a manifest states it: interpreter, platform, image, packages and lock digests. */
export interface EnvironmentManifest {
	python?: string;
	implementation?: string;
	platform?: string;
	architecture?: string;
	os?: string;
	image?: string;
	packages?: Record<string, string> | null;
	[extra: string]: unknown;
}

/** A registered environment: what a person or tool reported, digested, never rebuilt by registering it. */
export interface AnalysisEnvironment {
	id: string;
	revision: number;
	origin: "reported";
	digest: string;
	manifest: EnvironmentManifest;
	sourceRef?: string;
	reconstruction: "not_run";
	registeredAt: number;
}

export type ManifestStatus = "compatible" | "mismatch" | "missing";

export interface ManifestDifference {
	key: string;
	status: "mismatch" | "missing";
	recorded: unknown;
	observed: unknown;
}

/** Two caller manifests compared: only ever manifest_only, since nothing was observed or rebuilt. */
export interface ManifestComparison {
	status: ManifestStatus;
	scope: "manifest_only";
	reconstruction: "not_run";
	reasons: string[];
	differences: ManifestDifference[];
}

export type AnalysisType = "planned" | "exploratory";
export type AnalysisCheckStatus = "not_run" | "not_applicable" | "passed" | "failed" | "reported";

export interface PlanSensitivity {
	name: string;
	applicable: boolean;
	status: "not_run" | "not_applicable";
	reason?: string;
}

export interface AnalysisCheck {
	type: string;
	name?: string;
	status: AnalysisCheckStatus;
	reason?: string;
	reportedStatus?: string;
}

/** What a plan revision states; the store keeps it as written. */
export interface AnalysisPlanBody {
	title: string;
	analyses: string[];
	seed: number | null;
	parameters: Record<string, unknown>;
	cleaningRules: string[];
	exclusionRules: string[];
	analysisType: AnalysisType;
	sensitivity: PlanSensitivity[];
	checks: AnalysisCheck[];
	amendment?: { reason: string; fromRevision: number };
	[extra: string]: unknown;
}

/** One frozen plan revision; an amendment is a new revision naming the one it replaces and why. */
export interface AnalysisPlan extends AnalysisPlanBody {
	id: string;
	revision: number;
	status: RecordStatus;
	frozenAt: number;
}

/** A key result's recorded expectation: it matches iff abs(actual - value) <= max(absTolerance, relTolerance * abs(value)). */
export interface ExpectedResult {
	value: number;
	absTolerance: number;
	relTolerance: number;
}

export type KeyComparisonStatus = "matched" | "mismatched" | "missing" | "type_mismatch" | "not_finite";

export interface KeyComparison {
	key: string;
	status: KeyComparisonStatus;
	expected: number;
	actual?: unknown;
	absTolerance: number;
	relTolerance: number;
	tolerance: number;
	difference?: number;
	reason?: string;
}

export interface ResultComparison {
	status: "matched" | "mismatched" | "unavailable" | "not_requested";
	rule: string;
	keys: KeyComparison[];
	reason?: string;
}

export type ExecutionStatus = "succeeded" | "failed" | "timed_out" | "blocked";

/** What the managed worker observed; a blocked attempt has no observed receipt. */
export interface ManagedExecution {
	origin: "managed";
	receiptStatus: "observed" | "unknown";
	status: ExecutionStatus;
	startedAt: string | null;
	finishedAt: string | null;
	exitCode: number | null;
	signal: string | null;
	timedOut: boolean;
	stdout: string;
	stderr: string;
	stdoutTruncated?: boolean;
	stderrTruncated?: boolean;
	limits: { wallMs: number; cpuSeconds: number; memoryMb: number };
	isolation: { mode: "auto" | "unavailable"; backend?: string };
	reason?: string;
}

/** A run executed elsewhere, as reported: never executed here by its label. */
export interface ExternalExecution {
	origin: "external";
	receiptStatus: "reported" | "unknown";
	status: "reported";
	reportedStatus?: string;
	[extra: string]: unknown;
}

export interface RecordedEnvironment {
	origin: "reported" | "observed";
	environmentId?: string;
	runId?: string;
	digest: string;
	manifest: EnvironmentManifest;
}

/** The environment a run had: observed by the worker, compared with the recorded one, never installed. */
export interface RunEnvironment {
	origin: "observed" | "not_observed" | "reported";
	digest: string | null;
	observed: EnvironmentManifest | null;
	reconstruction: "compatible_existing" | "rebuilt" | "not_run" | "blocked";
	recorded?: RecordedEnvironment[];
	comparison: { status: ManifestStatus | "not_recorded" | "unavailable"; reasons: string[] };
	[extra: string]: unknown;
}

export interface RunOutput {
	path: string;
	sha256: string;
	size: number;
	artifactId: string;
}

export interface RunInputHash {
	inputId: string;
	mount: string;
	path: string;
	sha256: string;
	artifactId: string;
}

/** What a run retains; written once. */
export interface AnalysisRunBody {
	origin: "managed" | "external";
	title?: string;
	replayOf?: string;
	planId: string | null;
	planRevision: number | null;
	seed: number | null;
	parameters: Record<string, unknown>;
	analysisType: AnalysisType | "unknown";
	cleaningRules: string[];
	exclusionRules: string[];
	sensitivity: PlanSensitivity[];
	checks: AnalysisCheck[];
	command: string[];
	code: { path: string; sha256: string; artifactId: string; size: number } | null;
	codeHash: string | null;
	inputHashes: RunInputHash[];
	outputs: RunOutput[];
	keyResultsPath: string | null;
	expected: Record<string, ExpectedResult>;
	results: Record<string, unknown> | null;
	comparison: ResultComparison;
	methodValidity: "not_assessed";
	environment: RunEnvironment;
	execution: ManagedExecution | ExternalExecution;
	recordedAt: number;
	[extra: string]: unknown;
}

/** A retained run: immutable once written, a replay is a new run naming the one it replays. */
export interface AnalysisRun extends AnalysisRunBody {
	id: string;
}

export interface AnalysisSummary {
	/** Newest first, as each was written. */
	runs: AnalysisRun[];
	schemaVersion: number;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	/** Each plan at its newest revision. */
	plans: Array<AnalysisPlan & { revisions: number }>;
	inputs: AnalysisInput[];
	environments: AnalysisEnvironment[];
	notices: ResearchNotice[];
}

// The decisions domain: routes chosen and turned down, hypotheses, and materials bound to a scope (ADR-0090).

export const DECISION_KINDS = ["decision", "hypothesis", "material_binding"] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

export interface ResearchAlternative {
	route: string;
	disposition: "rejected" | "deferred" | "open";
	reason?: string;
	reconsiderWhen?: string[];
}

export type ResearchNextTest = string | { action: string; expected?: string; status?: "open" | "done" | "dropped" };

/** A record the body points at: a reference string, or a typed reference. */
export type DecisionEvidenceRef = string | ResearchRef;

export interface DecisionBody {
	title: string;
	route: string;
	reason: string;
	alternatives: ResearchAlternative[];
	evidenceRefs: DecisionEvidenceRef[];
	reconsiderWhen: string[];
	nextTests: ResearchNextTest[];
	/** The project direction's version when the revision was written, stamped by the service. */
	directionVersion?: string;
}

export const HYPOTHESIS_TEST_STATUSES = ["pending", "testing", "supported", "refuted", "inconclusive"] as const;
export type HypothesisTestStatus = (typeof HYPOTHESIS_TEST_STATUSES)[number];

export interface HypothesisBody {
	title?: string;
	statement: string;
	testStatus: HypothesisTestStatus;
	testRefs: DecisionEvidenceRef[];
	falsificationConditions: string[];
	nextTests: ResearchNextTest[];
	directionVersion?: string;
}


/**
 * What a later conversation restores of a scope's decisions: the effective revisions, the open hypotheses
 * and unplaced bindings, the proposals awaiting the owner and the rejected routes, cut to a byte budget
 * with a cursor to read on from instead of dropping the rest.
 */
export interface DecisionContext {
	scopeRef: ResearchScopeRef;
	schemaVersion: number;
	directionVersion: string | null;
	effective: ResearchRecord[];
	proposals: ResearchRecord[];
	unresolved: ResearchRecord[];
	rejected: ResearchRecord[];
	notices: ResearchNotice[];
	truncated: boolean;
	continuation: { cursor: number; omitted: number } | null;
}

// What the chat draws of a research tool's result.

export type ResearchSection =
	| "record"
	| "context"
	| "evidence_summary"
	| "evidence_impacts"
	| "verification_summary"
	| "report"
	| "analysis"
	| "analysis_summary"
	| "plan_history"
	| "quality_summary"
	| "capabilities"
	| "map"
	| "blocked"
	| "unavailable"
	| "refused";

/**
 * The analysis objects a view shows, each as the service returned it: the runs, plans, inputs and environments one
 * operation wrote or read (an analysis_summary's all of them, a plan_history's every revision of one plan, oldest
 * first), and an environment check's manifest comparison.
 */
export interface AnalysisView {
	runs: AnalysisRun[];
	plans: Array<AnalysisPlan & { revisions?: number }>;
	inputs: AnalysisInput[];
	environments: AnalysisEnvironment[];
	comparison?: ManifestComparison;
}

/** An authorized link a view offers: a record on the research page, an artifact under the scope's route, an action record, the page itself. */
export interface ResearchTarget {
	kind: "record" | "artifact" | "action" | "page";
	label: string;
	href: string;
	recordId?: string;
}

/** The scope's records by kind, and the newest of them: what a session outside a project sees instead of a direction. */
export interface ResearchIndex {
	counts: Record<string, number>;
	recent: Array<{ id: string; kind: string; revision: number; status: string; title: string }>;
}

/**
 * The server's view of one research tool result, in `details.researchView`: the chat renders it as it was
 * at `snapshotAt`, then refetches the scope's API, so nothing the agent retyped reaches the screen.
 */
export interface ResearchView {
	section: ResearchSection;
	operation: string;
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	records: StoredRecord[];
	notices: ResearchNotice[];
	capabilities: ResearchCapabilities;
	targets: ResearchTarget[];
	evidence?: EvidenceSummary;
	impacts?: ImpactItem[];
	quality?: QualitySummary;
	/** A finalized report, from a finalization or a read of its record. */
	report?: VerificationReport;
	summary?: VerificationSummary;
	index?: ResearchIndex;
	/** The project's research map, for a map read. */
	map?: ResearchMapView;
	/** Why a map read stopped: no selected project, or a domain this server does not deploy. */
	blocked?: ResearchBlocked;
	analysis?: AnalysisView;
	/** The decisions a context read restored. */
	decisions?: DecisionContext;
	/** The filter an impacts read was made with, so a refetch asks the same. */
	query?: Record<string, string>;
}

/** The API prefix of a scope: the project's research routes, or the signed-in person's inbox. */
export function researchApiBase(scope: ResearchScopeRef): string {
	return scope.kind === "project" ? `/api/projects/${encodeURIComponent(scope.projectId)}/research` : "/api/research/unassigned";
}

/** A scope as the research page's address and switcher name it: `project:<id>` or `unassigned`. */
export function researchScopeKey(scope: ResearchScopeRef): string {
	return scope.kind === "project" ? `project:${scope.projectId}` : "unassigned";
}

export function parseResearchScope(key: string | null | undefined): ResearchScopeRef | null {
	if (key === "unassigned") return { kind: "unassigned", collectionId: "inbox" };
	const projectId = key?.startsWith("project:") ? key.slice("project:".length).trim() : "";
	return projectId ? { kind: "project", projectId } : null;
}

/** The research page on a scope, optionally opened at one record or on the project's research map. */
export function researchPageHref(scope: ResearchScopeRef, recordId?: string, view?: "map"): string {
	const params = new URLSearchParams({ research: researchScopeKey(scope) });
	if (recordId) params.set("record", recordId);
	if (view) params.set("view", view);
	return `/?${params.toString()}`;
}

/** An artifact of the scope, under its owner-only route. */
export function researchArtifactHref(scope: ResearchScopeRef, artifactId: string): string {
	return `${researchApiBase(scope)}/artifacts/${artifactId.split("/").map(encodeURIComponent).join("/")}`;
}

/** The finalized report a record view carries, when one of its records is a report. */
export function reportIn(records: readonly StoredRecord[]): VerificationReport | undefined {
	for (const r of records) if (!r.unsupported && r.kind === VERIFICATION_REPORT_KIND) return reportOf(r);
	return undefined;
}

/** The scope's records by kind and its newest few, for a context read. */
export function researchIndexOf(reads: readonly RecordRead[], opts: { kind?: string; depth?: number } = {}): ResearchIndex {
	const counts: Record<string, number> = {};
	for (const { record } of reads) counts[record.kind] = (counts[record.kind] ?? 0) + 1;
	const listed = reads.map((r) => r.latest ?? r.record).filter((r) => !opts.kind || r.kind === opts.kind);
	const recent = listed
		.slice(-(opts.depth ?? 20))
		.reverse()
		.map((r) => ({ id: r.id, kind: r.kind, revision: r.revision, status: r.status, title: researchRecordTitle(r) }));
	return { counts, recent };
}

/** Every retained snapshot the values name, as links under the scope's artifact route, labeled with the original path. */
export function researchArtifactTargets(scope: ResearchScopeRef, values: unknown[]): ResearchTarget[] {
	const found = new Map<string, string>();
	const walk = (value: unknown): void => {
		if (Array.isArray(value)) value.forEach(walk);
		else if (value && typeof value === "object") {
			const v = value as Record<string, unknown>;
			for (const key of ["artifactId", "draftArtifactId", "sourceArtifactId"]) {
				const id = v[key];
				if (typeof id === "string" && !found.has(id)) found.set(id, typeof v.path === "string" ? v.path : id.split("/").pop()!);
			}
			for (const child of Object.values(v)) if (child && typeof child === "object") walk(child);
		}
	};
	walk(values);
	const base = researchApiBase(scope);
	return [...found].map(([id, label]) => ({ kind: "artifact", label, href: `${base}/artifacts/${id.split("/").map(encodeURIComponent).join("/")}` }));
}

/** What a record is called in a list: its title, text, quoted passage, original or report path, or rationale. */
export function researchRecordTitle(record: StoredRecord): string {
	if (record.unsupported) return record.reason;
	const b = record.body as Record<string, unknown>;
	const named = [
		b.title,
		b.text,
		b.statement,
		b.name,
		(b.passage as { text?: unknown } | undefined)?.text,
		(b.originalRef as { path?: unknown; url?: unknown } | undefined)?.path,
		(b.fileRef as { path?: unknown } | undefined)?.path,
		record.kind === VERIFICATION_REPORT_KIND ? (b.input as { path?: unknown } | undefined)?.path : undefined,
		b.rationale,
		b.description,
		b.correction,
	].find((v) => typeof v === "string" && v.trim());
	if (typeof named === "string") return named.slice(0, 200);
	if (record.kind === "quality_comparison") {
		const c = b as unknown as QualityComparisonBody;
		return `${c.baseline?.artifactRef?.path ?? ""} → ${c.candidate?.artifactRef?.path ?? ""}`;
	}
	return record.kind;
}

// The feedback domain (ADR-0089): sourced feedback, the regression cases verified feedback becomes, and bounded
// comparisons of existing outputs against them. Nothing here is a quality score.

/** A reference a feedback-domain body keeps: a file retained as a snapshot, or provenance that was not retrieved. */
export type FeedbackRef =
	| ({ kind: "file" } & FileRef)
	| { kind: "url"; url: string; title?: string; locator?: string; sourceStatus: "unretrieved" }
	| { kind: "citekey"; key: string; locator?: string; sourceStatus: "unlocated" }
	| { kind: "handle"; tool: string; handle: string; locator?: string; sourceStatus: "unretrieved"; handleStatus: "handle_unavailable" }
	| { kind: "record"; id: string; revision?: number; relation?: "supersedes" | "relates" | "evidence" }
	| { kind: "session"; id: string; messageId?: string };
export type FeedbackFileRef = Extract<FeedbackRef, { kind: "file" }>;

/** Record kinds of the feedback domain. `regression_case` and `quality_comparison` are written only by its operations. */
export const FEEDBACK_KINDS = ["feedback", "material_binding", "regression_case", "quality_comparison"] as const;

export type AssociationStatus = "proposed" | "confirmed" | "unresolved" | "ambiguous";

export interface MaterialBindingBody {
	title?: string;
	materialRef: FeedbackRef;
	targetScope: ResearchScopeRef;
	/** Where the material came from, in the writer's words. */
	origin?: string;
	rationale: string;
	/** `confirmed` only through the owner's confirmation. */
	associationStatus: AssociationStatus;
}

export const FEEDBACK_CATEGORIES = ["wrong_citation", "claim_mismatch", "missing_literature", "rejected_advice", "review_difference", "preference"] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

/** Categories that record what someone wanted, never what was factually wrong. */
export const PREFERENCE_CATEGORIES: readonly FeedbackCategory[] = ["preference", "rejected_advice"];

export type ReviewOrigin = "real" | "simulated" | "unknown";
export type FeedbackJudgment = "unverified" | "objective_verified" | "human_decided";
export type DatasetOrigin = "real" | "synthetic" | "unknown";

/** How a judgment was reached, each part as its writer stated it. */
export interface FeedbackVerification {
	method?: string;
	actor?: string;
	source?: FeedbackRef;
}

/** One piece of feedback on an output, with what it said, where it came from and how far it was checked. */
export interface FeedbackBody {
	category: FeedbackCategory;
	/** The original feedback: a review file, a message, a citation. */
	sourceRef: FeedbackRef;
	reviewOrigin: ReviewOrigin;
	description: string;
	correction: string;
	reason: string;
	judgment: FeedbackJudgment;
	verificationRefs: FeedbackRef[];
	datasetOrigin: DatasetOrigin;
	verification?: FeedbackVerification;
}

/** A literal check over one output file; it checks the declared fixture oracle, not whether a citation is truly correct. */
export type CaseGrader =
	| { type: "required_text"; value: string; fixtureHash?: string }
	| { type: "forbidden_text"; value: string; fixtureHash?: string }
	| { type: "regex_match"; pattern: string; fixtureHash?: string };

export const CASE_TYPES = ["objective", "human_judgment", "preference"] as const;
export type CaseType = (typeof CASE_TYPES)[number];

/** How a regression case was verified: a file whose retained snapshot proves it, or a person's recorded decision. */
export type CaseVerification =
	| { kind: "objective"; method: string; artifactRef: FeedbackFileRef; actor: string }
	| { kind: "human"; actor: string; source: FeedbackRef; decidedAt?: number }
	| { kind: "preference"; actor: string; source: FeedbackRef };

/** A verified piece of feedback turned into a check later outputs are run against. */
export interface RegressionCaseBody {
	feedbackId: string;
	feedbackRevision: number;
	category: FeedbackCategory;
	caseType: CaseType;
	reviewOrigin: ReviewOrigin;
	datasetOrigin: DatasetOrigin;
	judgment: FeedbackJudgment;
	verification: CaseVerification;
	grader: CaseGrader;
	/** The digest of the grader as stored; two runs compare only under the same one. */
	graderHash: string;
	fixtureHash: string | null;
	/** What a new output of a human-judged case waits for: a person, never the grader alone. */
	newOutputStatus: "graded" | "human_pending";
	/** Every source this case rests on, original feedback and corrections included. */
	sourceRefs: FeedbackRef[];
	correction: string;
	reason: string;
	/** Each wording of the correction the feedback went through, oldest first. */
	correctionHistory: Array<{ revision: number; status: RecordStatus; correction: string; reason: string }>;
	/** Whether the grader's literal appears in the verification snapshot; a literal check never proves a citation correct. */
	oracleTraced: boolean | null;
}

/** The versions an output was produced under, as the caller reported them. */
export interface VersionManifest {
	fixtureHash?: string;
	graderHash?: string;
	profileHash?: string;
	systemHash?: string;
	skillHash?: string;
	processHash?: string;
	packageHash?: string;
	model?: string;
	promptHash?: string;
	executionProvenance?: "verified" | "reported" | "unknown";
}

/** A server-observed record that an output was generated under a manifest; client labels are not receipts. */
export interface GenerationReceipt {
	receiptId: string;
	/** The output file's digest as the server observed it being written. */
	artifactSha256: string;
	/** The digest of what the server observed the generation ran under. */
	manifestHash: string;
	manifest: { profile: string; model: string | null; sessionId: string };
	observedBy: "transcript";
	sessionId: string;
	toolCallId: string;
}

/** Which generation a caller says produced an output; the server checks it against the session's transcript. */
export interface GenerationClaim {
	sessionId: string;
	toolCallId?: string;
}

export type CaseResult = "passed" | "failed" | "not_run" | "human_pending";

export interface ComparisonCaseResult {
	caseId: string;
	caseRevision: number;
	caseType: CaseType;
	baseline: CaseResult;
	candidate: CaseResult;
	baselineDetail: string;
	candidateDetail: string;
	/** True when the candidate fails an objective or human-graded case: the corrected error is back in it. */
	recurred: boolean;
}

export type ComparisonKind = "rescore_same_output" | "new_generated_outputs" | "artifact_comparison";
export type ComparisonAttribution = "artifact_only" | "version_comparison_bounded";

export interface ComparisonSide {
	/** The output as snapshotted for this comparison; grading reads the snapshot, never the live file. */
	artifactRef: FeedbackFileRef;
	/** What the caller reported; a reported manifest is provenance, not proof. */
	version: VersionManifest;
	manifestHash: string;
	/** The generation the caller named, if any, and whether the server could verify it. */
	claim: GenerationClaim | null;
	receipt: GenerationReceipt | null;
	receiptStatus: "none" | "verified" | "unverified";
	receiptReason?: string;
}

/** Two outputs graded against the same cases, with exactly what was and was not run. */
export interface QualityComparisonBody {
	status: "compared" | "incomparable";
	comparisonKind: ComparisonKind;
	attribution: ComparisonAttribution;
	baseline: ComparisonSide;
	candidate: ComparisonSide;
	cases: ComparisonCaseResult[];
	/** Objective and human-graded case IDs whose corrected error came back in the candidate. */
	recurrences: string[];
	/** Preference tests the candidate missed: never counted as factual errors. */
	preferenceMisses: string[];
	coverage: { caseIds: string[]; total: number };
	notRun: Array<{ caseId: string; reason: string }>;
	/** Exact hashes on each side, by name, so a reader sees what differed. */
	hashes: { baseline: VersionManifest; candidate: VersionManifest; differing: string[] };
	/** Never a number: a comparison bounds what changed on these cases, it does not measure quality. */
	qualityDelta: null;
	reasons: string[];
}

/** A comparison as the routes and tools return it: the stored body with its record's ID and revision. */
export type ComparisonView = QualityComparisonBody & { id: string; revision: number };

/** `bounded`: every verified real case has a comparison of distinct verified generations; still not a quality score. */
export type QualitySummaryStatus = "no_baseline" | "insufficient_coverage" | "bounded";

/** What the scope's stored cases and comparisons support saying about quality; never a score. */
export interface QualitySummary {
	scopeRef: ResearchScopeRef;
	snapshotAt: number;
	revision: string;
	status: QualitySummaryStatus;
	qualityDelta: null;
	reasons: string[];
	cases: { total: number; objective: number; humanJudgment: number; preference: number; verifiedReal: number; synthetic: number };
	feedback: { total: number; byCategory: Partial<Record<FeedbackCategory, number>> };
	comparisons: { total: number; incomparable: number; bounded: number; recurrences: string[] };
	/** Fixture-level outcome of the latest comparison of each case: mechanics, not research quality. */
	fixtureResults: Array<{ caseId: string; comparisonId: string; result: CaseResult }>;
}

// The map domain (ADR-0087): one project's whole research structure, derived from its records on every read.

/**
 * A reference after the service looked at its source: the snapshot it keeps (`artifactId`, served by GET
 * /artifacts/:id) and what the source looks like now. A file ref is `FileRef` plus the check; a URL, citekey or
 * handle is provenance that was not retrieved.
 */
export interface CheckedRef {
	kind?: string;
	id?: string;
	revision?: number;
	path?: string;
	base?: WorkspaceBase;
	sha256?: string;
	declaredSha256?: string;
	artifactId?: string;
	size?: number;
	capturedAt?: number;
	lastCheckedAt?: number;
	sourceStatus?: SourceStatus;
	url?: string;
	citekey?: string;
	handle?: string;
	/** The source's hash at the last check; null when it could not be read. */
	currentSha256?: string | null;
	/** Where a moved source was found, relative to its root. */
	movedTo?: string | null;
	[key: string]: unknown;
}

export type ResearchGroupKind = "question" | "literature" | "method" | "result" | "manuscript" | "decision" | "other";

export interface ResearchMapGroup {
	kind: ResearchGroupKind;
	label: string;
	nodeIds: string[];
}

export interface ResearchMapNode {
	id: string;
	kind: string;
	group: ResearchGroupKind;
	label: string;
	state: RecordStatus;
	authority: RecordAuthority;
	refs: CheckedRef[];
	sourceStatus: SourceStatus | null;
	/** The artifact id of the primary source's snapshot; served by GET /artifacts/:id. */
	snapshotRef: string | null;
	revision: number;
	headRevision: number;
	createdAt: number;
	target: { kind: "record"; id: string };
}

/** Where an edge comes from: a relation record, or a field of a record that names other records. */
export type EdgeDerivation = { kind: "relation"; recordId: string } | { kind: "field"; recordId: string; field: string };

export interface ResearchMapEdge {
	id: string;
	from: string;
	to: string;
	type: string;
	refs: CheckedRef[];
	/** `material` when the edge carries references; `unknown` when nothing recorded grounds it. */
	provenance: "material" | "unknown";
	derivedFrom: EdgeDerivation;
}

export type GapClassification = "user_confirmed" | "agent_inferred" | "not_recorded";

export interface ResearchGapBasis {
	rule: string;
	subjectRevision: number | null;
	checkedRefs: string[];
	assessmentRefs: string[];
	reason: string;
}

export interface ResearchMapGap {
	id: string;
	classification: GapClassification;
	/** A gap record, a rule over the records, or a group with no record at all. */
	derivation: "record" | "rule" | "coverage";
	title: string;
	subjectId: string | null;
	subjectKind: string | null;
	reason: string;
	proposedCategory: string | null;
	refs: CheckedRef[];
	nextActions: string[];
	basis: ResearchGapBasis;
	target: { kind: "record"; id: string } | null;
	revision: number | null;
	headRevision: number | null;
	authority: RecordAuthority | null;
	origin: RecordOrigin | null;
	createdAt: number | null;
}

export interface ResearchCoverage {
	paths: Array<{ path: string; sourceStatus: SourceStatus; recordIds: string[] }>;
	unknownDomains: ResearchDomain[];
	/** Material bindings nobody has confirmed: the association queue. */
	notAssociated: Array<{ id: string; materialRef: unknown; associationStatus: string; rationale: string | null; target: { kind: "record"; id: string } }>;
}

/** The whole-project map, derived on every read. */
export interface ResearchMapView {
	schema: { version: number };
	scopeRef: ResearchScopeRef;
	root: { kind: "project"; id: string; label: string };
	groups: ResearchMapGroup[];
	nodes: ResearchMapNode[];
	edges: ResearchMapEdge[];
	gaps: ResearchMapGap[];
	coverage: ResearchCoverage;
	/** Made of the records' revisions and the sources' current hashes: a changed file changes it. */
	revision: string;
	snapshotAt: number;
	lastCheckedAt: number;
	capabilities: ResearchCapabilities;
	focus: { id: string; depth: number } | null;
	notices: ResearchNotice[];
}

/** A map asked for without a selected project: a handled blocked result, never an unassigned overview. */
export interface ScopeRequired {
	code: "scope_required";
	status: "blocked";
	projectSelectionRequired: true;
	scopeRef: null;
	records: never[];
	nodes: never[];
	edges: never[];
	gaps: never[];
}

export const SCOPE_REQUIRED: ScopeRequired = { code: "scope_required", status: "blocked", projectSelectionRequired: true, scopeRef: null, records: [], nodes: [], edges: [], gaps: [] };

/** Why a research view stopped short of its result. */
export interface ResearchBlocked {
	code: "scope_required" | "domain_unavailable";
	domain?: ResearchDomain;
	reason: string;
}
